import React, { useState } from 'react'
import IframeResizer from 'iframe-resizer-react'
import PropTypes from 'prop-types'
import scriptLoader from 'react-async-script-loader'
import config from 'config'
import { useForm } from 'react-hook-form'

import { extractNumber } from '@cred/shared/utils'

import LoadingSpinner from 'components/loading-spinner'
import Pagination from 'components/styled-pagination'
import { useViewport } from 'components/hooks'

import FilterModal from '../components/filter-modal'
import HYSFilterForm from './components/hys-filter-form'
import HYSLendersList from './components/lenders-list'
import { generateFilterText, getPageStats, getPartnerDefaultFilterValues } from '../utils'
import { loadLenders } from './components/lenders-data'

const TABLET = 568
const paginationName = 'HYS-rate-table'
const getTilePerPage = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { width } = useViewport()

  return width >= TABLET ? 3 : 2
}
const INITIAL_VALUES = {
  balanceAmount: 10000,
}

const HYSRateTable = ({ variation, widgetContext, theme, isScriptLoaded }) => {
  const defaultValues = {
    ...INITIAL_VALUES,
    ...getPartnerDefaultFilterValues(widgetContext, 'high-yield-savings'),
  }
  const { control, reset, getValues, formState } = useForm({ defaultValues, mode: 'onChange' })

  const [page, setPage] = useState(0)
  const [filterValues, setFilterValues] = useState(defaultValues)
  const lenders = loadLenders({ isScriptLoaded })
  const { pageCount, pageFrom, pageTo, showPagination } = getPageStats(
    variation,
    lenders,
    page,
    getTilePerPage(),
  )

  const cancel = (closeModal) => {
    closeModal()
    // Setting timeout so the form doesn't reset as the modal is disappearing
    setTimeout(() => {
      reset(filterValues)
    }, 500)
  }

  const onUpdate = (closeModal) => {
    const { balanceAmount } = getValues()
    setFilterValues({ balanceAmount: extractNumber(balanceAmount) })
    closeModal()
  }

  const onPageChange = (_, selectedPage) => {
    setPage(selectedPage)
  }

  const onReset = () => {
    reset(defaultValues)
    setFilterValues(defaultValues)
  }

  return (
    <div>
      <FilterModal
        formName="HYS-filter-form"
        filterText={generateFilterText(filterValues, 'high-yield-savings')}
      >
        {({ closeModal }) => (
          <HYSFilterForm
            control={control}
            update={() => onUpdate(closeModal)}
            cancel={() => cancel(closeModal)}
            withFooter
            invalid={formState.isDirty && !formState.isValid}
          />
        )}
      </FilterModal>
      <HYSLendersList
        lenders={lenders}
        pageFrom={pageFrom}
        pageTo={pageTo}
        balanceAmount={extractNumber(filterValues.balanceAmount)}
        resetFilters={onReset}
        theme={theme}
      />
      {showPagination && (
        <Pagination onChange={onPageChange} pageCount={pageCount} name={paginationName} />
      )}
    </div>
  )
}

HYSRateTable.propTypes = {
  isScriptLoaded: PropTypes.bool,
  variation: PropTypes.string,
  widgetContext: PropTypes.object,
  theme: PropTypes.string,
}

const HYSRateTableBankrate = ({ showIframe = false }) => {
  const [loading, setLoading] = useState(true)
  return (
    <>
      {loading && <LoadingSpinner open staticPosition />}
      <IframeResizer
        onInit={() => {
          setLoading(false)
        }}
        src="https://www.myfinance.com/as/77ee0b85-293d-480a-92cd-c1d81de95345?utm_medium=embed&imr=https://www.foxbusiness.com/&utm_campaign=fox-savings-multi&subId=PLACESUBIDHERE"
        className="myFinance-iframe"
        title="Advertisement"
        id="myfiWidget_2"
        scrolling="no"
        style={{
          overflow: 'hidden',
          width: '1px',
          minWidth: '100%',
          border: 'none',
          display: showIframe && !loading ? 'block' : 'none',
        }}
      />
    </>
  )
}

HYSRateTableBankrate.propTypes = { showIframe: PropTypes.bool }

export { HYSRateTable as HYSRateTableNoHoc, HYSRateTableBankrate }

export default scriptLoader(config.ratesLibraryUrl)(HYSRateTable)
